import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import "./style.css";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("pt-BR", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const formatPrice = (price) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(price);

export default function EventFeed() {
  const { user } = useSelector((state) => ({ ...state }));

  // Estados principais
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [category, setCategory] = useState("all");
  const [eventType, setEventType] = useState("all");
  const [sort, setSort] = useState("date_asc");
  const [isFree, setIsFree] = useState(false);

  // Estados de modal
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showEventFeed, setShowEventFeed] = useState(false);

  // Compra
  const [selectedTier, setSelectedTier] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [pin, setPin] = useState("");
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState("");
  const [purchaseSuccess, setPurchaseSuccess] = useState("");

  // Posts
  const [posts, setPosts] = useState([]);
  const [newPostContent, setNewPostContent] = useState("");
  const [postLoading, setPostLoading] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);

  const categories = [
    { id: "all", name: "Todas", icon: "🎯" },
    { id: "music", name: "Música", icon: "🎵" },
    { id: "sports", name: "Esportes", icon: "⚽" },
    { id: "arts", name: "Artes", icon: "🎨" },
    { id: "food", name: "Gastronomia", icon: "🍽️" },
    { id: "technology", name: "Tecnologia", icon: "💻" },
    { id: "business", name: "Negócios", icon: "💼" },
    { id: "education", name: "Educação", icon: "📚" },
    { id: "health", name: "Saúde", icon: "🏥" },
    { id: "entertainment", name: "Entretenimento", icon: "🎬" },
    { id: "networking", name: "Networking", icon: "🤝" },
    { id: "charity", name: "Caridade", icon: "❤️" },
    { id: "other", name: "Outros", icon: "📌" }
  ];

  const eventTypes = [
    { id: "all", name: "Todos" },
    { id: "physical", name: "Presencial" },
    { id: "online", name: "Online" },
    { id: "hybrid", name: "Híbrido" }
  ];

  const sortOptions = [
    { id: "date_asc", name: "Data (Próximos)" },
    { id: "date_desc", name: "Data (Mais Distantes)" },
    { id: "price_asc", name: "Preço (Menor)" },
    { id: "price_desc", name: "Preço (Maior)" },
    { id: "popular", name: "Mais Populares" }
  ];

  const fetchEvents = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_BACKEND_URL}/events/feed?page=${pageNum}&limit=12`;

      if (category !== "all") url += `&category=${category}`;
      if (eventType !== "all") url += `&eventType=${eventType}`;
      if (sort) url += `&sort=${sort}`;
      if (isFree) url += `&isFree=true`;

      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      if (pageNum === 1) {
        setEvents(data.events || []);
      } else {
        setEvents((prev) => [...prev, ...(data.events || [])]);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching events:", error);
      setLoading(false);
    }
  }, [category, eventType, sort, isFree, user.token]);

  useEffect(() => {
    fetchEvents(1);
  }, [category, eventType, sort, isFree, fetchEvents]);

  const handlePurchaseTicket = async (e) => {
    e.preventDefault();
    if (!selectedTier || !quantity || !pin) {
      setPurchaseError("Preencha todos os campos");
      return;
    }

    setPurchaseLoading(true);
    setPurchaseError("");
    setPurchaseSuccess("");

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/events/${selectedEvent._id}/purchase`,
        {
          tierId: selectedTier._id,
          quantity,
          walletPin: pin
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      setPurchaseSuccess("Compra realizada com sucesso!");
      setShowTicketModal(false);
      setPin("");
      setQuantity(1);
      setSelectedTier(null);
    } catch (error) {
      setPurchaseError(
        error.response?.data?.message || "Erro ao processar a compra"
      );
    } finally {
      setPurchaseLoading(false);
    }
  };

  const openTicketModal = (event) => {
    setSelectedEvent(event);
    setShowTicketModal(true);
  };

  const openEventFeed = (event) => {
    setSelectedEvent(event);
    setShowEventFeed(true);
    fetchEventPosts(event._id);
  };

  const fetchEventPosts = (eventId) => {
    setPostsLoading(true);
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/events/${eventId}/posts`, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { page: 1, limit: 50, sort: "recent" }
      })
      .then((res) => {
        setPosts(res.data.posts || []);
        setPostsLoading(false);
      })
      .catch((err) => {
        console.error("Erro ao buscar posts:", err);
        setPostsLoading(false);
      });
  };

  const handleCreatePost = (e) => {
    e.preventDefault();
    if (!newPostContent.trim()) return;

    setPostLoading(true);
    axios
      .post(
        `${process.env.REACT_APP_BACKEND_URL}/events/${selectedEvent._id}/posts`,
        {
          content: newPostContent,
          postType: "update",
          visibility: "public"
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      )
      .then(() => {
        setNewPostContent("");
        fetchEventPosts(selectedEvent._id);
        setPostLoading(false);
      })
      .catch((err) => {
        console.error("Erro ao criar post:", err);
        setPostLoading(false);
      });
  };

  const handleLikePost = (postId, alreadyLiked) => {
    axios
      .post(
        `${process.env.REACT_APP_BACKEND_URL}/events/${selectedEvent._id}/posts/${postId}/${
          alreadyLiked ? "unlike" : "like"
        }`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      )
      .then(() => {
        fetchEventPosts(selectedEvent._id);
      })
      .catch((err) => console.error("Erro ao curtir:", err));
  };

  if (loading && events.length === 0) {
    return (
      <div className="event_feed_loading">
        <div className="event_loader"></div>
        <span>Carregando eventos...</span>
      </div>
    );
  }

  return (
    <div className="event_feed">
      <div className="event_feed_header">
        <h1>🎉 Eventos</h1>
        <p>Descubra eventos incríveis próximos a você</p>
      </div>

      {/* Filtros */}
      <div className="simple_filter_bar">
        <div className="filter_buttons">
          {categories.map((filter) => (
            <button
              key={filter.id}
              className={`filter_btn ${category === filter.id ? "active" : ""}`}
              onClick={() => setCategory(filter.id)}
            >
              {filter.icon} {filter.name}
            </button>
          ))}
        </div>
      </div>

      <div className="event_filters_row">
        <select value={eventType} onChange={(e) => setEventType(e.target.value)}>
          {eventTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>

        <select value={sort} onChange={(e) => setSort(e.target.value)}>
          {sortOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>

        <label>
          <input
            type="checkbox"
            checked={isFree}
            onChange={(e) => setIsFree(e.target.checked)}
          />
          Apenas Gratuitos
        </label>
      </div>

      {events.length === 0 ? (
        <div className="event_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
          </svg>
          <h3>Nenhum evento encontrado</h3>
          <p>Ajuste os filtros ou volte mais tarde!</p>
        </div>
      ) : (
        <div className="event_grid">
          {events.map((event) => (
            <div
              key={event._id}
              className="event_card"
              onClick={() => setSelectedEvent(event)}
            >
              <div className="event_image">
                <img
                  src={
                    event.coverImage ||
                    event.images?.[0]?.url ||
                    "/images/default-event.png"
                  }
                  alt={event.title}
                />
                <div className="event_date_badge">
                  <span className="event_date_day">
                    {new Date(event.startDate).getDate()}
                  </span>
                  <span className="event_date_month">
                    {new Date(event.startDate)
                      .toLocaleDateString("pt-BR", { month: "short" })
                      .toUpperCase()}
                  </span>
                </div>
                {event.isFree && <span className="event_badge_free">GRÁTIS</span>}
                {event.isSoldOut && (
                  <span className="event_badge_soldout">ESGOTADO</span>
                )}
              </div>
              <div className="event_card_content">
                <span className="event_type">
                  {categories.find((c) => c.id === event.category)?.name ||
                    "Evento"}
                </span>
                <h3>{event.title}</h3>
                <div className="event_info">
                  <span className="event_time">
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      width="14"
                      height="14"
                    >
                      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                    </svg>
                    {formatDate(event.startDate)}
                  </span>
                  <span className="event_location">
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      width="14"
                      height="14"
                    >
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                    </svg>
                    {event.location?.city || "Online"}
                  </span>
                </div>
                <div className="event_footer">
                  <span className="event_price">
                    {event.isFree ? "Grátis" : formatPrice(event.minPrice)}
                  </span>
                  <div className="event_actions">
                    <button
                      className="event_btn_feed"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEventFeed(event);
                      }}
                      title="Feed do evento"
                    >
                      💬 Feed
                    </button>
                    {!event.isSoldOut && (
                      <button
                        className="event_buy_btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openTicketModal(event);
                        }}
                      >
                        🎫 Comprar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: Detalhes do Evento */}
      {selectedEvent && !showTicketModal && !showEventFeed && (
        <div
          className="event_modal_overlay"
          onClick={() => setSelectedEvent(null)}
        >
          <div
            className="event_modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close_btn"
              onClick={() => setSelectedEvent(null)}
            >
              ×
            </button>
            <div className="event_modal_content">
              <img
                src={
                  selectedEvent.coverImage ||
                  selectedEvent.images?.[0]?.url ||
                  "/images/default-event.png"
                }
                alt={selectedEvent.title}
              />
              <div className="event_details">
                <h2>{selectedEvent.title}</h2>
                <p className="event_date_time">
                  📅 {new Date(selectedEvent.startDate).toLocaleDateString("pt-BR")}
                  {selectedEvent.startTime && ` às ${selectedEvent.startTime}`}
                </p>
                <p className="event_location">
                  📍 {selectedEvent.location?.city || "Online"}
                </p>
                <p className="event_description">
                  {selectedEvent.description}
                </p>

                {selectedEvent.ticketTiers &&
                  selectedEvent.ticketTiers.length > 0 && (
                    <div className="event_tiers">
                      <h3>Ingressos Disponíveis</h3>
                      {selectedEvent.ticketTiers.map((tier, index) => (
                        <div key={index} className="event_tier_item">
                          <div>
                            <strong>{tier.name}</strong>
                            <p>{tier.description}</p>
                          </div>
                          <div>
                            <span className="tier_price">
                              {formatPrice(tier.price)}
                            </span>
                            <span className="tier_availability">
                              {tier.quantity - tier.sold} disponíveis
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                <button
                  className="event_modal_buy_btn"
                  onClick={() => openTicketModal(selectedEvent)}
                  disabled={selectedEvent.isSoldOut}
                >
                  {selectedEvent.isSoldOut
                    ? "Esgotado"
                    : "🎫 Comprar Ingressos"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Compra de Tickets */}
      {showTicketModal && selectedEvent && (
        <div
          className="event_modal_overlay"
          onClick={() => setShowTicketModal(false)}
        >
          <div
            className="event_modal event_modal_purchase"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="event_modal_close"
              onClick={() => setShowTicketModal(false)}
            >
              ✕
            </button>

            <h2>🎫 Comprar Ingressos</h2>
            <h3>{selectedEvent.title}</h3>

            <form onSubmit={handlePurchaseTicket}>
              {purchaseError && (
                <div className="alert alert_error">{purchaseError}</div>
              )}
              {purchaseSuccess && (
                <div className="alert alert_success">{purchaseSuccess}</div>
              )}

              <div className="form_group">
                <label>Tipo de Ingresso:</label>
                <select
                  value={selectedTier?._id || ""}
                  onChange={(e) => {
                    const tier = selectedEvent.ticketTiers.find(
                      (t) => t._id === e.target.value
                    );
                    setSelectedTier(tier);
                  }}
                  required
                >
                  <option value="">Selecione um tipo</option>
                  {selectedEvent.ticketTiers &&
                    selectedEvent.ticketTiers.map((tier) => (
                      <option key={tier._id} value={tier._id}>
                        {tier.name} - {formatPrice(tier.price)} (
                        {tier.quantity - tier.sold} disponíveis)
                      </option>
                    ))}
                </select>
              </div>

              <div className="form_group">
                <label>Quantidade:</label>
                <input
                  type="number"
                  min="1"
                  max={selectedTier?.maxPerUser || 10}
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value))}
                  required
                />
              </div>

              <div className="total_amount">
                <strong>Total:</strong>
                <span>
                  {formatPrice((selectedTier?.price || 0) * quantity)}
                </span>
              </div>

              <div className="form_group">
                <label>PIN da Wallet (6 dígitos):</label>
                <input
                  type="password"
                  placeholder="000000"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  maxLength="6"
                  pattern="[0-9]{6}"
                  required
                />
                <small>💳 Pagamento exclusivo via Socializa Wallet</small>
              </div>

              <button type="submit" disabled={purchaseLoading}>
                {purchaseLoading ? "Processando..." : "Confirmar Compra"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Feed do Evento */}
      {showEventFeed && selectedEvent && (
        <div
          className="event_modal_overlay"
          onClick={() => setShowEventFeed(false)}
        >
          <div
            className="event_feed_modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="event_modal_close"
              onClick={() => setShowEventFeed(false)}
            >
              ✕
            </button>

            <div className="event_modal_header">
              <h2>💬 Feed do Evento</h2>
              <p>{selectedEvent.title}</p>
            </div>

            {/* Criar Post */}
            {user && (
              <form onSubmit={handleCreatePost} className="event_post_form">
                <div className="post_input_group">
                  <img
                    src={user?.picture || "/avatar.png"}
                    alt={user?.first_name}
                    className="post_avatar"
                  />
                  <div className="post_input_wrapper">
                    <textarea
                      placeholder="Compartilhe algo sobre este evento..."
                      value={newPostContent}
                      onChange={(e) => setNewPostContent(e.target.value)}
                      maxLength="5000"
                      rows="3"
                    />
                    <div className="post_actions">
                      <button
                        type="submit"
                        disabled={postLoading || !newPostContent.trim()}
                        className="post_submit_btn"
                      >
                        {postLoading ? "Postando..." : "Postar"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}

            {/* Feed de Posts */}
            <div className="event_posts_feed">
              {postsLoading ? (
                <div className="posts_loading">Carregando posts...</div>
              ) : posts.length === 0 ? (
                <div className="posts_empty">
                  Nenhum post neste evento. Seja o primeiro a comentar!
                </div>
              ) : (
                posts.map((post) => (
                  <div key={post._id} className="event_post_item">
                    {/* Header do Post */}
                    <div className="post_header">
                      <img
                        src={post.author?.picture || "/avatar.png"}
                        alt={post.author?.first_name}
                        className="post_avatar_small"
                      />
                      <div className="post_author_info">
                        <strong>
                          {post.author?.first_name} {post.author?.last_name}
                        </strong>
                        <small>
                          {new Date(post.createdAt).toLocaleDateString(
                            "pt-BR"
                          )}
                        </small>
                      </div>
                      {post.isEventAnnouncement && (
                        <span className="post_badge_official">📢 Oficial</span>
                      )}
                    </div>

                    {/* Conteúdo */}
                    <p className="post_content">{post.content}</p>

                    {/* Imagens */}
                    {post.images && post.images.length > 0 && (
                      <div className="post_images">
                        {post.images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img.url || img}
                            alt={`Post ${idx}`}
                          />
                        ))}
                      </div>
                    )}

                    {/* Reações */}
                    <div className="post_stats">
                      <span>❤️ {post.likes?.length || 0} curtidas</span>
                      <span>💬 {post.comments?.length || 0} comentários</span>
                    </div>

                    {/* Ações */}
                    <div className="post_actions_footer">
                      <button
                        className={`post_action_btn ${
                          post.userLiked ? "liked" : ""
                        }`}
                        onClick={() =>
                          handleLikePost(post._id, post.userLiked)
                        }
                      >
                        ❤️ {post.userLiked ? "Descurtir" : "Curtir"}
                      </button>
                      <button className="post_action_btn">
                        💬 Comentar
                      </button>
                      <button className="post_action_btn">
                        ↗️ Compartilhar
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
